import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Routes, useLocation } from 'react-router-dom';

import { AccessControl } from '../../../components';
import { useWorkspaceContext } from '../../../context';
import { InvoicingAction, useInvoicingAuthz } from '../../../hooks/useInvoicingAuthz';
import ErrorLogger from '../../../providers/ErrorLogger';
import RouteGuard from '../../../routes/RouteGuard';
import CustomizeInvoiceFields from '../components/CustomizeInvoiceFields';
import FormstackIntegration from '../components/FormstackIntegration';
import InvoiceAdd from '../components/InvoiceAdd';
import InvoiceCandidateList from '../components/InvoiceCandidateList';
import InvoiceCreateOptions from '../components/InvoiceCreateOptions';
import InvoiceCreateSelection from '../components/InvoiceCreateSelection';
import InvoiceDetails from '../components/InvoiceDetails';
import InvoiceExtraSettings from '../components/InvoiceExtraSettings';
import InvoiceListingPage from '../components/InvoiceListingPage';
import { InvoiceTemplateForm } from '../components/InvoiceTemplateForm';
import InvoiceType from '../components/InvoiceType';
import { ManageInvoiceTemplates } from '../components/ManageInvoiceTemplates';
import PaymentTypeDetails from '../components/PaymentTypeDetails';
import TaxTypeDetails from '../components/TaxTypeDetails ';
import TermsAndConditions from '../components/TermsAndConditions';
import WorkspaceSettingsContextProvider from '../WorkspaceSettingsContext';

export const InvoicingRoutes = () => {
  const canAddTemplate = useInvoicingAuthz(InvoicingAction.ADD_INVOICE_TEMPLATE);
  const canEditTemplate = useInvoicingAuthz(InvoicingAction.EDIT_INVOICE_TEMPLATE);
  const locationState = useLocation().state as { readonly?: boolean };
  const readonly = !!locationState?.readonly;
  const workspace = useWorkspaceContext();

  return (
    <>
      <Routes>
        <Route
          path="/settings/payment_type"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <PaymentTypeDetails />
              </ErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/invoice_type"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceType />
              </ErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/customize-invoice-fields"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <CustomizeInvoiceFields />
              </ErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/terms-and-conditions"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <TermsAndConditions />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/settings/tax_type"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <TaxTypeDetails />
              </ErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/settings/invoice_extra"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceExtraSettings />
              </ErrorBoundary>
            </RouteGuard>
          }
        />
        <Route
          path="/list"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceListingPage />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/settings/invoice_details"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceDetails />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/:id"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceDetails />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path=":id/edit/"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <WorkspaceSettingsContextProvider>
                  <InvoiceAdd />
                </WorkspaceSettingsContextProvider>
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/add"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <WorkspaceSettingsContextProvider>
                  <InvoiceAdd />
                </WorkspaceSettingsContextProvider>
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/addoptions"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceCreateOptions />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        <Route
          path="/candidatelist"
          element={
            <RouteGuard>
              <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                <InvoiceCandidateList />
              </ErrorBoundary>
            </RouteGuard>
          }
        />

        {workspace.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' && (
          <>
            <Route
              path="/template/new"
              element={
                canAddTemplate ? (
                  <RouteGuard>
                    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                      <InvoiceTemplateForm />
                    </ErrorBoundary>
                  </RouteGuard>
                ) : (
                  <AccessControl />
                )
              }
            />
            <Route
              path="/template/:id"
              element={
                !readonly && !canEditTemplate ? (
                  <AccessControl />
                ) : (
                  <RouteGuard>
                    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                      <InvoiceTemplateForm />
                    </ErrorBoundary>
                  </RouteGuard>
                )
              }
            />
            <Route
              path="/template/formstack"
              element={
                <RouteGuard>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <FormstackIntegration />
                  </ErrorBoundary>
                </RouteGuard>
              }
            />
            <Route
              path="/template"
              element={
                <RouteGuard>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <ManageInvoiceTemplates />
                  </ErrorBoundary>
                </RouteGuard>
              }
            />
            <Route
              path="/invoice/template"
              element={
                <RouteGuard>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <InvoiceExtraSettings />
                  </ErrorBoundary>
                </RouteGuard>
              }
            />
            <Route
              path="/formstack"
              element={
                <RouteGuard>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <FormstackIntegration />
                  </ErrorBoundary>
                </RouteGuard>
              }
            />
            <Route
              path="/template/select"
              element={
                canAddTemplate ? (
                  <RouteGuard>
                    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                      <InvoiceCreateSelection />
                    </ErrorBoundary>
                  </RouteGuard>
                ) : (
                  <AccessControl />
                )
              }
            />
          </>
        )}
      </Routes>
    </>
  );
};
